import { useDateTimeToLocaleString as useDateTimeToLocaleStringImported } from '@hooks/useDateTimeWithLocale'
import { DateTime } from 'luxon'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { DatePickerDropdown } from '../date-picker-dropdown/DatePickerDropdown'
import { MonthCalendar } from 'components/navigation/month-calendar'
import { useRenderSelectedWeek } from './utils/useRenderSelectedWeek'
import { getWeekStart } from '@utils/dates'

interface Props {
  hideWeekStartsOnSelector?: boolean
  onMenuHiddenChange?: (hidden: boolean) => void
  onWeekSelect: (options: {
    start: DateTime
    weekStartsOn: 'sunday' | 'monday'
  }) => void
  selectedWeek: DateTime
  useDateTimeToLocaleString?: typeof useDateTimeToLocaleStringImported
  variant?: 'dark' | 'light'
  weekStartsOn: 'monday' | 'sunday'
}

export const WeekSelector: FC<Props> = ({
  hideWeekStartsOnSelector = false,
  onMenuHiddenChange,
  onWeekSelect,
  selectedWeek,
  useDateTimeToLocaleString = useDateTimeToLocaleStringImported,
  variant = 'dark',
  weekStartsOn,
}) => {
  const { t } = useTranslation()
  const startOfSelectedWeek = getWeekStart({ date: selectedWeek, weekStartsOn })
  const previousWeek = startOfSelectedWeek.minus({ days: 7 })
  const nextWeek = startOfSelectedWeek.plus({ days: 7 })
  const toLocaleString = useDateTimeToLocaleString()
  const renderSelectedWeek = useRenderSelectedWeek(toLocaleString)

  return (
    <DatePickerDropdown
      content={renderSelectedWeek(startOfSelectedWeek)}
      nextButtonTitle={t('components.weekSelector.goToNextWeek')}
      onMenuHiddenChange={onMenuHiddenChange}
      onNextButtonClick={() => onWeekSelect({ start: nextWeek, weekStartsOn })}
      onPreviousButtonClick={() =>
        onWeekSelect({ start: previousWeek, weekStartsOn })
      }
      previousButtonTitle={t('components.weekSelector.goToPreviousWeek')}
      title={t('components.weekSelector.selectADifferentWeek')}
      variant={variant}
    >
      {({ menuHidden }) => {
        if (menuHidden) return null

        return (
          <MonthCalendar
            className="absolute left-0 z-20 top-12 border-neutral-200 rounded-lg bg-white border w-[280px] drop-shadow"
            hideWeekStartsOnSelector={hideWeekStartsOnSelector}
            initiallyDisplayedMonth={selectedWeek}
            onWeekSelect={onWeekSelect}
            selectedWeek={selectedWeek}
            useDateTimeToLocaleString={useDateTimeToLocaleString}
            weekStartsOn={weekStartsOn}
          />
        )
      }}
    </DatePickerDropdown>
  )
}
